import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
} from '@mui/material';
import { Map, Delete, Edit, Add } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import api from '../services/api';
import { format } from 'date-fns';

interface MappingTemplate {
  id: number;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  columnMappingsCount?: number;
}

export const MappingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [mappings, setMappings] = useState<MappingTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState<MappingTemplate | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadMappings();
  }, []);

  const loadMappings = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/mappings/templates');
      const data = response.data.map((m: any) => ({
        ...m,
        columnMappingsCount: m.columnMappings?.length || 0,
      }));
      setMappings(data);
    } catch (err: any) {
      console.error('Failed to load mappings:', err);
      setError(err.response?.data?.error || 'Nem sikerült betölteni a mapping-eket');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (mapping: MappingTemplate, event: React.MouseEvent) => {
    event.stopPropagation();
    setMappingToDelete(mapping);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!mappingToDelete) return;

    try {
      setDeleting(true);
      await api.delete(`/mappings/templates/${mappingToDelete.id}`);

      // Remove from local state immediately for better UX
      setMappings(prev => prev.filter(m => m.id !== mappingToDelete.id));

      setDeleteDialogOpen(false);
      setMappingToDelete(null);
    } catch (err: any) {
      console.error('Failed to delete mapping:', err);

      let errorMessage = 'Nem sikerült törölni a mapping-et';

      if (err.response) {
        errorMessage = err.response.data?.error ||
                      err.response.data?.message ||
                      `Hiba (${err.response.status}): ${err.response.statusText}`;
      } else if (err.request) {
        errorMessage = 'Nem sikerült kapcsolódni a szerverhez. Kérjük, ellenőrizze az internetkapcsolatot.';
      } else {
        errorMessage = err.message || errorMessage;
      }

      alert(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setMappingToDelete(null);
  };

  const handleEdit = (mappingId: number) => {
    navigate(`/mapping-editor?templateId=${mappingId}`);
  };

  return (
    <>
      <Header />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Map /> Mapping-ek
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Excel oszlop leképezési sablonok kezelése
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/mapping-editor')}
            size="large"
          >
            Új Mapping
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : mappings.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <Map sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Még nincs mapping sablon
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Hozzon létre egy mapping sablont az Excel fájlok oszlopainak automatikus leképezéséhez!
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/mapping-editor')}
              size="large"
            >
              Első Mapping Létrehozása
            </Button>
          </Paper>
        ) : (
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                {mappings.length} mapping sablon
              </Typography>
            </Box>
            <List>
              {mappings.map((mapping) => (
                <ListItem
                  key={mapping.id}
                  button
                  onClick={() => handleEdit(mapping.id)}
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        edge="end"
                        aria-label="edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(mapping.id);
                        }}
                        color="primary"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={(e) => handleDeleteClick(mapping, e)}
                        color="error"
                        sx={{
                          '&:hover': {
                            backgroundColor: 'error.light',
                            color: 'error.contrastText',
                          },
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  }
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    pr: 12,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle1" component="span" fontWeight={600}>
                          {mapping.name}
                        </Typography>
                        <Chip
                          label={`${mapping.columnMappingsCount} oszlop`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={
                      <Box component="span">
                        {mapping.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {mapping.description}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" component="span">
                          Létrehozva: {format(new Date(mapping.createdAt), 'yyyy-MM-dd HH:mm')}
                          {mapping.updatedAt && mapping.updatedAt !== mapping.createdAt && (
                            <> • Frissítve: {format(new Date(mapping.updatedAt), 'yyyy-MM-dd HH:mm')}</>
                          )}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>

            {/* Delete Confirmation Dialog */}
            <Dialog
              open={deleteDialogOpen}
              onClose={handleDeleteCancel}
              aria-labelledby="delete-dialog-title"
              aria-describedby="delete-dialog-description"
            >
              <DialogTitle id="delete-dialog-title">
                Mapping törlése
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="delete-dialog-description">
                  Biztosan törölni szeretnéd a mapping sablont?
                  {mappingToDelete && (
                    <>
                      <br />
                      <br />
                      <strong>{mappingToDelete.name}</strong>
                      <br />
                      {mappingToDelete.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {mappingToDelete.description}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {mappingToDelete.columnMappingsCount} oszlop leképezés lesz törölve
                      </Typography>
                    </>
                  )}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleDeleteCancel} disabled={deleting}>
                  Mégse
                </Button>
                <Button
                  onClick={handleDeleteConfirm}
                  color="error"
                  variant="contained"
                  disabled={deleting}
                  startIcon={deleting ? <CircularProgress size={16} /> : <Delete />}
                >
                  {deleting ? 'Törlés...' : 'Törlés'}
                </Button>
              </DialogActions>
            </Dialog>
          </Paper>
        )}
      </Container>
    </>
  );
};
